import * as Yup from 'yup';
import { CreateVerificationPayload, RequestType } from '../../../data/verification/verification.models';

// Types and interfaces

export interface FormValues {
    request_type: RequestType[];
    business_legal_name: string;
    business_street_line_1: string;
    business_street_line_2: string;
    business_city: string;
    business_state: string;
    business_zip_code: string;
    business_country: string;
    business_website: string;
    business_ein: string;
    applicant_first_name: string;
    applicant_last_name: string;
    applicant_email: string;
    applicant_phone: string;
    applicant_date_of_birth: string;
    applicant_last_4_ssn: string;
    applicant_role: string;
}

// Constants
export const requestTypes = [
    RequestType.Overall,
    RequestType.KYB,
    RequestType.FirstParty,
    RequestType.Synthetic,
    // RequestType.ThirdParty
];

export const initialValues: FormValues = {
    request_type: [RequestType.Overall, RequestType.KYB, RequestType.FirstParty, RequestType.Synthetic],
    business_legal_name: '',
    business_street_line_1: '',
    business_street_line_2: '',
    business_city: '',
    business_state: '',
    business_zip_code: '',
    business_country: 'US',
    business_website: '',
    business_ein: '',
    applicant_first_name: '',
    applicant_last_name: '',
    applicant_email: '',
    applicant_phone: '',
    applicant_date_of_birth: '',
    applicant_last_4_ssn: '',
    applicant_role: ''
};

// Full validation schema (legacy - keeping for backward compatibility)
export const validationSchema = Yup.object({
    request_type: Yup.array().of(Yup.string()).min(1, 'At least one request type is required'),
    business_legal_name: Yup.string().required('Legal name is required'),
    business_street_line_1: Yup.string().required('Street address is required'),
    business_city: Yup.string().required('City is required'),
    business_state: Yup.string().required('State is required'),
    business_zip_code: Yup.string().required('ZIP code is required'),

    // Conditional applicant validation
    applicant_first_name: Yup.string().when('request_type', {
        is: (requestTypes: RequestType[]) => isApplicantInfoRequired(requestTypes),
        then: (schema) => schema.required('First name is required'),
        otherwise: (schema) => schema
    }),
    applicant_last_name: Yup.string().when('request_type', {
        is: (requestTypes: RequestType[]) => isApplicantInfoRequired(requestTypes),
        then: (schema) => schema.required('Last name is required'),
        otherwise: (schema) => schema
    }),
    applicant_email: Yup.string().when('request_type', {
        is: (requestTypes: RequestType[]) => isApplicantInfoRequired(requestTypes),
        then: (schema) => schema.email('Invalid email format').required('Email is required'),
        otherwise: (schema) => schema.email('Invalid email format')
    }),
});

// Helper function to convert MM/DD/YYYY to YYYY-MM-DD
const convertDateFormat = (dateString: string): string => {
    if (!dateString) return '';

    // Parse MM/DD/YYYY format
    const match = dateString.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (match) {
        const [, month, day, year] = match;
        // Convert to YYYY-MM-DD format with zero-padding
        return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    }

    return dateString; // Return as-is if format doesn't match
};

export const transformFormValuesToPayload = (values: FormValues): CreateVerificationPayload => {
    return {
        request_type: values.request_type,
        business: {
            legal_name: values.business_legal_name,
            address: {
                street_line_1: values.business_street_line_1,
                ...(values.business_street_line_2 && { street_line_2: values.business_street_line_2 }),
                city: values.business_city,
                state: values.business_state,
                zip_code: values.business_zip_code,
                country: values.business_country,
            },
            ...(values.business_website && { website: values.business_website }),
            ...(values.business_ein && { ein: values.business_ein })
        },
        ...(values.applicant_first_name || values.applicant_last_name || values.applicant_email ||
            values.applicant_phone || values.applicant_date_of_birth || values.applicant_last_4_ssn ||
            values.applicant_role) && {
            applicants: [{
                first_name: values.applicant_first_name || '',
                last_name: values.applicant_last_name || '',
                email: values.applicant_email || '',
                phone: values.applicant_phone || '',
                date_of_birth: values.applicant_date_of_birth ? convertDateFormat(values.applicant_date_of_birth) : '',
                last_4_ssn: values.applicant_last_4_ssn || '',
                role: values.applicant_role || ''
            }]
        }
    };
};

// Field validation functions
export const getFieldWarning = (fieldName: string, value: string): string | null => {
    if (!value) return null;

    switch (fieldName) {
        case 'business_state':
            if (!/^[A-Z]{2}$/.test(value)) {
                return 'Please use 2-letter US state code (e.g., NY, CA)';
            }
            break;
        case 'business_zip_code':
            if (!/^\d{5}(-\d{4})?$/.test(value)) {
                return 'Please use valid US ZIP code format (12345 or 12345-6789)';
            }
            break;
        case 'business_website':
            if (!/^https?:\/\/.+\..+/.test(value)) {
                return 'Please use valid website format (https://example.com)';
            }
            break;
        case 'business_ein':
            if (!/^\d{2}-\d{7}$/.test(value)) {
                return 'Please use EIN format: XX-XXXXXXX';
            }
            break;
        case 'applicant_phone':
            if (!/^\+?1?[-.\s]?(\(?\d{3}\)?[-.\s]?)?\d{3}[-.\s]?\d{4}$/.test(value)) {
                return 'Please use valid US phone number format';
            }
            break;
        case 'applicant_email':
            if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
                return 'Please use valid email format';
            }
            break;
        case 'applicant_last_4_ssn':
            if (!/^\d{4}$/.test(value)) {
                return 'Please enter exactly 4 digits';
            }
            break;
    }
    return null;
};

// File handling functions
export const validateCsvFile = (file: File): boolean => {
    return Boolean(file && file.type === 'text/csv');
};

export const createFileInput = (onChange: (event: React.ChangeEvent<HTMLInputElement>) => void) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.csv';
    input.onchange = onChange as any;
    return input;
};

export const downloadTemplate = () => {
    const link = document.createElement('a');
    link.href = '/verifications_bulk_template.csv';
    link.download = 'verifications_bulk_template.csv';
    link.click();
};

// Helper function to determine if applicant info is required based on request types
export const isApplicantInfoRequired = (requestTypes: RequestType[]): boolean => {
    // Applicant info is required ONLY if Overall or ThirdParty are selected
    // FirstParty, KYB, Synthetic do NOT require applicant info

    // If Overall or ThirdParty are included, applicant info is required
    return requestTypes.includes(RequestType.ThirdParty);
};

// Form validation helpers
export const isFormValid = (values: FormValues, isValid: boolean): boolean => {
    return Boolean(isValid &&
        values.request_type.length > 0 &&
        values.business_legal_name &&
        values.business_street_line_1 &&
        values.business_city &&
        values.business_state &&
        values.business_zip_code);
};

// State formatting helpers
export const formatStateInput = (value: string): string => {
    return value.toUpperCase();
};

export const formatSsnInput = (value: string): string => {
    return value.replace(/\D/g, '');
};

export const formatPhoneInput = (value: string): string => {
    const digits = value.replace(/\D/g, '').slice(0, 10);
    if (digits.length > 6) {
        return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    }
    if (digits.length > 3) {
        return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    }
    return digits;
};

export const formatEinInput = (value: string): string => {
    const digits = value.replace(/\D/g, '').slice(0, 9);
    if (digits.length > 2) {
        return `${digits.slice(0, 2)}-${digits.slice(2)}`;
    }
    return digits;
};

// Success message formatters
export const formatBulkUploadMessage = (data: any): string => {
    const failedCount = data.total_rows - data.processed_successfully;
    return `Upload completed! ${data.processed_successfully} successful, ${failedCount} failed${data.skipped_rows > 0 ? `, ${data.skipped_rows} skipped` : ''}`;
};